import { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import Header from "../../components/Header";
import LoadingOverlay from "../../components/LoadingOverlay";
import FirmFlowTable from "../../components/mytasks/FirmFlowTable";
import ReportFiltersCard, {
    ReportFilterField,
} from "../../components/reports/ReportFiltersCard";
import Sidebar from "../../components/Sidebar";
import { formatDate } from "../../helpers/formatDate";
import { getDueDateReport } from "../../services/analytics/getDueDateReport";
import { getDueDateReportServices } from "../../services/analytics/getDueDateReportServices";
import { getEmployees } from "../../services/getEmployees";
import { getServiceDepartments } from "../../services/service/getServiceDepartments";
import {
    DUE_DATE_STATUSES,
    getDueDateStatusFromDate,
} from "../../helpers/dueDateStatus";
import {
    getDueTodayTextClass,
    isDueTodayLabel,
} from "../../helpers/dueTodayAppearance";
import { sortDueDateRows } from "../../helpers/sortDueDateRows";
import { usePermissions } from "../../context/PermissionsContext";

const ALLOWED_DUE_DATE_ALIASES = ["pastDue", "next7days", "thereafter"];
const DEFAULT_ID = "00000000-0000-0000-0000-000000000000";
const JOB_SORT_OPTIONS = {
    dueDateKeys: ["dueDate"],
    yearKeys: ["clientServiceSubscriptionYear", "serviceYear", "year"],
    serviceNameKeys: [
        "clientServiceSubscriptionServiceName",
        "serviceName",
        "service",
    ],
};
const SERVICE_SORT_OPTIONS = {
    dueDateKeys: ["subscriptionEndDate"],
    yearKeys: ["serviceYear", "year"],
    serviceNameKeys: ["serviceName", "service"],
};

const createDueDateStatuses = () =>
    DUE_DATE_STATUSES.filter((status) =>
        ALLOWED_DUE_DATE_ALIASES.includes(status.alias),
    ).map((status) => ({
        ...status,
        numberOfJobs: 0,
        numberOfServices: 0,
        jobs: [],
        services: [],
        dueTodayCount: 0,
        dueTodayJobs: [],
        dueTodayServices: [],
    }));

const ITEMS = [
    {
        value: 1,
        label: "Services",
        is_showing: true,
    },
    {
        value: 2,
        label: "Steps",
        is_showing: true,
    },
];

const getDueTodayCount = (report) =>
    report?.dueTodayCount ??
    report?.numberDueToday ??
    report?.today ??
    report?.numberOfToday ??
    0;

const normalizeDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const isTodayLocal = (value) => {
    const date = normalizeDate(value);
    if (!date) return false;

    const today = normalizeDate(new Date());
    return today ? date.getTime() === today.getTime() : false;
};

const getDueTodayJobs = (report) =>
    report?.dueTodayJobs ??
    report?.jobs?.filter((job) => isTodayLocal(job?.dueDate)) ??
    [];

const getDueTodayServices = (report) =>
    report?.dueTodayServices ??
    report?.services?.filter((service) =>
        isTodayLocal(service?.subscriptionEndDate),
    ) ??
    [];

const DueDateReport = () => {
    const { can, permissionsLoading } = usePermissions();
    const canReadServices = can("client_services.read");
    const canReadSteps = can("steps.read");
    const availableItems = useMemo(
        () =>
            ITEMS.filter((item) =>
                item.label === "Services" ? canReadServices : canReadSteps,
            ),
        [canReadServices, canReadSteps],
    );
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [dueDateReports, setDueDateReports] = useState(createDueDateStatuses());
    const [selectedReport, setSelectedReport] = useState(null);
    const [selectedReportStatus, setSelectedReportStatus] = useState(null);
    const [selectedReportName, setSelectedReportName] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        itemValue: ITEMS[0].value,
        departmentId: DEFAULT_ID,
        employeeId: DEFAULT_ID,
    });
    const [appliedFilters, setAppliedFilters] = useState({
        itemValue: ITEMS[0].value,
        departmentId: DEFAULT_ID,
        employeeId: DEFAULT_ID,
    });

    const [departments, setDepartments] = useState([
        {
            value: DEFAULT_ID,
            label: "All",
            is_showing: true,
        },
    ]);

    const [employees, setEmployees] = useState([
        {
            value: DEFAULT_ID,
            label: "All",
            is_showing: true,
        },
    ]);
    const bucketSectionRef = useRef(null);
    const tableSectionRef = useRef(null);
    const shouldScrollToResultsRef = useRef(false);
    const shouldScrollToSelectionRef = useRef(false);

    const selectedItem = useMemo(
        () =>
            availableItems.find((item) => item.value === filters.itemValue) ||
            availableItems[0] ||
            null,
        [availableItems, filters.itemValue],
    );

    const appliedSelectedItem = useMemo(
        () =>
            availableItems.find(
                (item) => item.value === appliedFilters.itemValue,
            ) ||
            availableItems[0] ||
            null,
        [appliedFilters.itemValue, availableItems],
    );

    const selectedEmployee = useMemo(
        () =>
            employees.find((employee) => employee.value === filters.employeeId) ||
            employees[0],
        [employees, filters.employeeId],
    );

    const selectedDepartment = useMemo(
        () =>
            departments.find(
                (department) => department.value === filters.departmentId,
            ) || departments[0],
        [departments, filters.departmentId],
    );

    const onHandleClickReport = (jobs, reportName) => {
        shouldScrollToSelectionRef.current = true;
        setSelectedReport(jobs || []);
        setSelectedReportStatus(null);
        setSelectedReportName(reportName);
    };

    const onHandleClickReportStatus = (services, reportName) => {
        shouldScrollToSelectionRef.current = true;
        setSelectedReportStatus(services || []);
        setSelectedReport(null);
        setSelectedReportName(reportName);
    };

    const renderReportCountButton = (count, onClick) => (
        <button
            type="button"
            onClick={onClick}
            className="mt-4 font-sans antialiased font-normal tracking-normal text-white transition-transform duration-150 cursor-pointer text-7xl hover:scale-105 hover:text-slate-200"
        >
            {count}
        </button>
    );

    const renderDueTodayButton = (count, onClick) => (
        <button
            type="button"
            onClick={onClick}
            className={`mt-3 text-base font-semibold transition-transform duration-150 cursor-pointer hover:scale-105 ${getDueTodayTextClass({
                surface: "warning",
                interactive: true,
            })}`}
        >
            Due today: {count}
        </button>
    );

    useEffect(() => {
        if (permissionsLoading) return;
        const fallbackValue = availableItems[0]?.value;
        if (!fallbackValue) {
            setIsLoading(false);
            return;
        }
        if (!availableItems.some((item) => item.value === filters.itemValue)) {
            setFilters((current) => ({
                ...current,
                itemValue: fallbackValue,
            }));
            setAppliedFilters((current) => ({
                ...current,
                itemValue: fallbackValue,
            }));
        }
    }, [availableItems, filters.itemValue, permissionsLoading]);

    useEffect(() => {
        let isMounted = true;

        async function fetchDepartments() {
            if (
                permissionsLoading ||
                (!canReadServices && !canReadSteps)
            )
                return;
            try {
                const response = await getServiceDepartments();
                if (!isMounted || !response?.data?.success) return;

                const departmentsArray = response.data.departments.map(
                    (department) => ({
                        value: department.id,
                        label: department.name,
                        is_showing: true,
                    }),
                );

                setDepartments([
                    {
                        value: DEFAULT_ID,
                        label: "All",
                        is_showing: true,
                    },
                    ...departmentsArray,
                ]);
            } catch (err) {
                if (!isMounted) return;
                setError(err);
            }
        }

        fetchDepartments();

        return () => {
            isMounted = false;
        };
    }, [canReadServices, canReadSteps, permissionsLoading]);

    useEffect(() => {
        let isMounted = true;

        async function fetchEmployees() {
            if (
                permissionsLoading ||
                (!canReadServices && !canReadSteps)
            )
                return;
            try {
                const response = await getEmployees();
                const data = response?.data?.employees;

                if (!isMounted || !Array.isArray(data)) return;

                const options = data.map((employee) => ({
                    value: employee.employeeId,
                    label: `${employee.firstName} ${employee.lastName}`,
                    is_showing: true,
                }));

                setEmployees([
                    {
                        value: DEFAULT_ID,
                        label: "All",
                        is_showing: true,
                    },
                    ...options,
                ]);
            } catch (err) {
                if (!isMounted) return;
                setError(err);
            }
        }

        fetchEmployees();

        return () => {
            isMounted = false;
        };
    }, [canReadServices, canReadSteps, permissionsLoading]);

    const getDueDateReportJobs = async () => {
        if (!canReadSteps) return;
        const nextReports = await Promise.all(
            createDueDateStatuses().map(async (report) => {
                const request = {
                    rangeName: report.alias,
                    departmentId: appliedFilters.departmentId,
                    employeeId: appliedFilters.employeeId,
                    includeInactive: false,
                };

                const response = await getDueDateReport(request);
                const apiSuccess =
                    response?.data?.success ?? response?.success ?? false;
                const apiReport = response?.data?.report ?? response?.report ?? null;

                if (!apiSuccess || !apiReport) {
                    return report;
                }

                const sortedJobs = sortDueDateRows(
                    apiReport.jobs || [],
                    JOB_SORT_OPTIONS,
                );
                const sortedDueTodayJobs = sortDueDateRows(
                    getDueTodayJobs(apiReport),
                    JOB_SORT_OPTIONS,
                );

                return {
                    ...report,
                    numberOfJobs: apiReport.numberOfJobs ?? 0,
                    jobs: sortedJobs,
                    dueTodayCount:
                        report.alias === "next7days"
                            ? getDueTodayCount(apiReport)
                            : 0,
                    dueTodayJobs:
                        report.alias === "next7days"
                            ? sortedDueTodayJobs
                            : [],
                };
            }),
        );

        setDueDateReports(nextReports);
    };

    const getServicesReport = async () => {
        if (!canReadServices) return;
        const nextReports = await Promise.all(
            createDueDateStatuses().map(async (report) => {
                const request = {
                    rangeName: report.alias,
                    departmentId: appliedFilters.departmentId,
                    employeeId: appliedFilters.employeeId,
                    includeInactive: false,
                };

                const response = await getDueDateReportServices(request);
                const apiSuccess =
                    response?.data?.success ?? response?.success ?? false;
                const apiReport = response?.data?.report ?? response?.report ?? null;

                if (!apiSuccess || !apiReport) {
                    return report;
                }

                const sortedServices = sortDueDateRows(
                    apiReport.services || [],
                    SERVICE_SORT_OPTIONS,
                );
                const sortedDueTodayServices = sortDueDateRows(
                    getDueTodayServices(apiReport),
                    SERVICE_SORT_OPTIONS,
                );

                return {
                    ...report,
                    numberOfServices: apiReport.numberOfServices ?? 0,
                    services: sortedServices,
                    dueTodayCount:
                        report.alias === "next7days"
                            ? getDueTodayCount(apiReport)
                            : 0,
                    dueTodayServices:
                        report.alias === "next7days"
                            ? sortedDueTodayServices
                            : [],
                };
            }),
        );

        setDueDateReports(nextReports);
    };

    const initializeReports = () => {
        setSelectedReport(null);
        setSelectedReportStatus(null);
        setSelectedReportName(null);
    };

    const onChangeItem = (item) => {
        if (!item) return;
        setFilters((currentFilters) => ({
            ...currentFilters,
            itemValue: item.value,
        }));
    };

    const onChangeDepartment = (department) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            departmentId: department?.value || DEFAULT_ID,
        }));
    };

    const onChangeEmployee = (employee) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            employeeId: employee?.value || DEFAULT_ID,
        }));
    };

    const handleApplyFilters = () => {
        initializeReports();
        shouldScrollToResultsRef.current = true;
        setAppliedFilters({ ...filters });
    };

    useEffect(() => {
        let isMounted = true;

        async function loadReport() {
            if (permissionsLoading || !appliedSelectedItem) {
                if (!permissionsLoading) setIsLoading(false);
                return;
            }
            setIsLoading(true);
            setError(null);

            try {
                if (appliedSelectedItem.label === "Steps") {
                    await getDueDateReportJobs();
                } else {
                    await getServicesReport();
                }
            } catch (err) {
                if (!isMounted) return;
                setError(err);
            } finally {
                if (!isMounted) return;
                setIsLoading(false);
            }
        }

        loadReport();

        return () => {
            isMounted = false;
        };
    }, [
        appliedFilters,
        appliedSelectedItem,
        canReadServices,
        canReadSteps,
        permissionsLoading,
    ]);

    useEffect(() => {
        if (isLoading || !shouldScrollToResultsRef.current) {
            return;
        }

        if (bucketSectionRef.current) {
            bucketSectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }

        shouldScrollToResultsRef.current = false;
    }, [isLoading, dueDateReports]);

    useEffect(() => {
        const hasSelectedTable =
            selectedReport !== null || selectedReportStatus !== null;

        if (!hasSelectedTable || !shouldScrollToSelectionRef.current) {
            return;
        }

        if (tableSectionRef.current) {
            tableSectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }

        shouldScrollToSelectionRef.current = false;
    }, [selectedReport, selectedReportStatus]);

    const columns = useMemo(
        () => [
            {
                Header: "Service",
                accessor: "clientServiceSubscriptionServiceName",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Client",
                accessor: "clientName",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Step",
                accessor: "name",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Year",
                accessor: "clientServiceSubscriptionYear",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Due date",
                accessor: "dueDate",
                Cell: (props) => {
                    const value = props.value;

                    if (!value) {
                        return (
                            <span className="text-xs text-slate-500">
                                Not assigned
                            </span>
                        );
                    }

                    const status = getDueDateStatusFromDate(value);
                    const formattedDate = formatDate(value, {
                        dateStyle: "short",
                    });

                    return (
                        <a
                            rel="noreferrer"
                            href={`/clients/service/${props.row.original.clientServiceSubscriptionId}/client/${props.row.original.clientId}`}
                            className="rounded-full"
                        >
                            <span
                                className={`inline-flex items-center px-3 py-1 text-xs font-semibold text-white rounded-full ${
                                    status?.bgClass || "bg-slate-400"
                                }`}
                            >
                                {formattedDate}
                            </span>
                        </a>
                    );
                },
            },
        ],
        [],
    );

    const columnsForServices = useMemo(
        () => [
            {
                Header: "Service",
                accessor: "serviceName",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.id}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Client",
                accessor: "clientName",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.id}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Year",
                accessor: "year",
                Cell: (props) => (
                    <a
                        rel="noreferrer"
                        href={`/clients/service/${props.row.original.id}/client/${props.row.original.clientId}`}
                        className="rounded-full text-gray hover:text-blue-500"
                    >
                        {props.value}
                    </a>
                ),
            },
            {
                Header: "Due date",
                accessor: "subscriptionEndDate",
                Cell: (props) => {
                    const value = props.value;

                    if (!value) {
                        return (
                            <span className="text-xs text-slate-500">
                                Not assigned
                            </span>
                        );
                    }

                    const { id, clientId, dueDateSubLabel } = props.row.original;
                    const status = getDueDateStatusFromDate(value);
                    const formattedDate = formatDate(value, {
                        dateStyle: "short",
                    });

                    return (
                        <a
                            rel="noreferrer"
                            href={`/clients/service/${id}/client/${clientId}`}
                            className="inline-flex flex-col items-start"
                        >
                            <span
                                className={`inline-flex items-center px-3 py-1 text-xs font-semibold text-white rounded-full ${
                                    status?.bgClass || "bg-slate-400"
                                }`}
                            >
                                {formattedDate}
                            </span>

                            {!!dueDateSubLabel && (
                                <span className="mt-1 text-xs font-medium text-slate-600">
                                    {dueDateSubLabel}
                                </span>
                            )}
                        </a>
                    );
                },
            },
            {
                Header: "Assigned to",
                accessor: "assignedEmployee",
                Cell: (props) => {
                    const { id, clientId } = props.row.original;
                    const value = props.value;
                    const label =
                        typeof value === "string"
                            ? value
                            : value
                              ? `${value.firstName || ""} ${
                                    value.lastName || ""
                                }`.trim()
                              : "";

                    return (
                        <a
                            rel="noreferrer"
                            href={`/clients/service/${id}/client/${clientId}`}
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {label || "Not assigned"}
                        </a>
                    );
                },
            },
        ],
        [],
    );

    return (
        <>
            <LoadingOverlay isLoading={permissionsLoading || isLoading} />
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            <div className="flex flex-col items-start max-w-3xl">
                                <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                    Due Date
                                </h1>
                            </div>

                            <ReportFiltersCard
                                actions={
                                    <button
                                        type="button"
                                        onClick={handleApplyFilters}
                                        disabled={isLoading}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white transition bg-teal-500 rounded-lg shadow-sm hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        Apply Filters
                                    </button>
                                }
                                fieldsClassName="grid grid-cols-1 gap-4 xl:grid-cols-4"
                            >
                                <ReportFilterField label="Assignment Type">
                                    <Select
                                        value={selectedItem}
                                        onChange={onChangeItem}
                                        options={availableItems}
                                        classNamePrefix="select"
                                        isSearchable={false}
                                        isDisabled={isLoading}
                                    />
                                </ReportFilterField>

                                <ReportFilterField label="Employee">
                                    <Select
                                        value={selectedEmployee}
                                        onChange={onChangeEmployee}
                                        options={employees}
                                        classNamePrefix="select"
                                        isSearchable
                                        isDisabled={isLoading}
                                    />
                                </ReportFilterField>

                                <ReportFilterField label="Department">
                                    <Select
                                        value={selectedDepartment}
                                        onChange={onChangeDepartment}
                                        options={departments}
                                        classNamePrefix="select"
                                        isSearchable={false}
                                        isDisabled={isLoading}
                                    />
                                </ReportFilterField>
                            </ReportFiltersCard>

                            {error && (
                                <div className="p-3 mt-6 text-sm text-red-700 border border-red-200 rounded-lg shadow-sm bg-red-50">
                                    {error.message || "Error loading due date report."}
                                </div>
                            )}

                            <div ref={bucketSectionRef} className="mt-6">
                                {appliedSelectedItem?.label === "Steps" ? (
                                    <div className="flex flex-row mb-12 overflow-x-auto">
                                        {dueDateReports.map((status, index) => (
                                            <div
                                                key={`${status.alias}-${index}`}
                                                className={`relative flex flex-col bg-clip-border rounded-xl text-white shadow-gray-900/20 shadow-md w-full p-7 mx-12 ${status.bgClass}`}
                                            >
                                                <div className="relative flex flex-col items-center pb-6 m-0 mb-4 overflow-hidden text-center text-gray-700 bg-transparent border-b rounded-none shadow-none bg-clip-border border-white/10">
                                                    <p className="block font-sans text-sm antialiased font-normal leading-normal text-white uppercase">
                                                        {status.name}
                                                    </p>
                                                    <div className="flex justify-center gap-1">
                                                        <span className="mt-2 text-2xl"></span>
                                                        {renderReportCountButton(
                                                            status.numberOfJobs,
                                                            () =>
                                                                onHandleClickReport(
                                                                    status.jobs,
                                                                    status.name,
                                                                ),
                                                        )}
                                                    </div>

                                                    <p className="mt-4 text-lg text-white">
                                                        STEPS
                                                    </p>
                                                    {status.alias === "next7days" &&
                                                        renderDueTodayButton(
                                                            status.dueTodayCount ?? 0,
                                                            () =>
                                                                onHandleClickReport(
                                                                    status.dueTodayJobs,
                                                                    "Due today",
                                                                ),
                                                        )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : appliedSelectedItem?.label === "Services" ? (
                                    <div className="flex flex-row mb-12 overflow-x-auto">
                                        {dueDateReports.map((status, index) => (
                                            <div
                                                key={`${status.alias}-${index}`}
                                                className={`relative flex flex-col rounded-xl text-white shadow-gray-900/20 shadow-md w-full p-7 mx-12 ${status.bgClass}`}
                                            >
                                                <div className="relative flex flex-col items-center overflow-hidden text-center text-gray-700">
                                                    <p className="block font-sans text-sm antialiased font-normal leading-normal text-white uppercase">
                                                        {status.name}
                                                    </p>
                                                    <div className="flex justify-center gap-1">
                                                        <span className="mt-2 text-2xl"></span>
                                                        {renderReportCountButton(
                                                            status.numberOfServices,
                                                            () =>
                                                                onHandleClickReportStatus(
                                                                    status.services,
                                                                    status.name,
                                                                ),
                                                        )}
                                                    </div>

                                                    <p className="mt-4 text-white text-md">
                                                        SERVICES
                                                    </p>
                                                    {status.alias === "next7days" &&
                                                        renderDueTodayButton(
                                                            status.dueTodayCount ?? 0,
                                                            () =>
                                                                onHandleClickReportStatus(
                                                                    status.dueTodayServices,
                                                                    "Due today",
                                                                ),
                                                        )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-4 text-sm bg-white border rounded-sm text-slate-500 border-slate-200">
                                        You don't have permission to view due date
                                        data.
                                    </div>
                                )}

                                <div
                                    ref={tableSectionRef}
                                    className="relative flex flex-col mx-0 lg:mx-12"
                                >
                                    {selectedReport !== null &&
                                        appliedSelectedItem?.label === "Steps" && (
                                        <>
                                            <h2
                                                className={`pb-8 text-2xl font-bold ${
                                                    isDueTodayLabel(
                                                        selectedReportName,
                                                    )
                                                        ? getDueTodayTextClass()
                                                        : "text-black"
                                                }`}
                                            >
                                                {selectedReportName} Steps
                                            </h2>
                                            <div
                                                onClickCapture={(event) => {
                                                    if (
                                                        !canReadServices &&
                                                        event.target.closest("a")
                                                    ) {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                    }
                                                }}
                                                aria-disabled={!canReadServices}
                                            >
                                                <FirmFlowTable
                                                    columns={columns}
                                                    data={selectedReport}
                                                />
                                            </div>
                                        </>
                                        )}

                                    {selectedReportStatus !== null &&
                                        appliedSelectedItem?.label ===
                                            "Services" && (
                                        <>
                                            <h2
                                                className={`pb-8 text-2xl font-bold ${
                                                    isDueTodayLabel(
                                                        selectedReportName,
                                                    )
                                                        ? getDueTodayTextClass()
                                                        : "text-black"
                                                }`}
                                            >
                                                {selectedReportName}{" "}
                                                {appliedSelectedItem?.label}
                                            </h2>
                                            <FirmFlowTable
                                                columns={columnsForServices}
                                                data={selectedReportStatus}
                                            />
                                        </>
                                        )}
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </>
    );
};

export default DueDateReport;
